/**
 * 근로자의 날(노동절) 합산 토글 Hook.
 * 설계문서: required/근로자의날.md §9
 *
 * - shareBookId 없으면(=비로그인/무료 사용자) Firestore 호출 스킵, localStorage 만 사용
 * - 로그인 시 onSnapshot 으로 양방향 실시간 동기화 (Flutter 앱과 같은 문서 공유)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  setLaborDayExcludedEmployees,
  subscribeLaborDayExcludedEmployees,
} from '../services/laborDayService';

const localStorageKey = (accountNumber: number) => `labor_day_excluded:${accountNumber}`;

const readLocal = (accountNumber: number): string[] => {
  try {
    const raw = localStorage.getItem(localStorageKey(accountNumber));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    return [];
  }
};

const writeLocal = (accountNumber: number, ids: string[]) => {
  try {
    localStorage.setItem(localStorageKey(accountNumber), JSON.stringify(ids));
  } catch {
    /* quota / privacy mode — silently ignore */
  }
};

export interface UseLaborDayToggleResult {
  /** 노동절 수당 합산이 OFF 인 직원 ID 목록 */
  excludedIds: string[];
  /** 직원의 토글 ON 여부 (= excludedIds 에 없으면 ON) */
  isIncluded: (employeeId: string) => boolean;
  /** 직원의 토글 ON/OFF 설정. true=ON(합산), false=OFF(제외) */
  setIncluded: (employeeId: string, included: boolean) => Promise<void>;
  loading: boolean;
}

/**
 * @param shareBookId  Firestore 동기화 키. null 이면 localStorage 만 사용.
 * @param accountNumber 가계부 번호.
 */
export function useLaborDayToggle(
  shareBookId: string | null,
  accountNumber: number
): UseLaborDayToggleResult {
  const [excludedIds, setExcludedIds] = useState<string[]>(() => readLocal(accountNumber));
  const [loading, setLoading] = useState<boolean>(!!shareBookId);

  // pending writes 가 도중에 onSnapshot 으로 덮어써지는 것 방지용
  const pendingWriteRef = useRef<string[] | null>(null);

  // 가계부 전환 시 localStorage 의 다른 키 값으로 즉시 갱신
  useEffect(() => {
    setExcludedIds(readLocal(accountNumber));
  }, [accountNumber]);

  // Firestore 실시간 구독 (로그인 사용자만)
  useEffect(() => {
    if (!shareBookId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = subscribeLaborDayExcludedEmployees(
      shareBookId,
      accountNumber,
      (ids) => {
        // 직전 write 와 다른 결과만 들어오면(즉, 다른 클라이언트가 변경한 경우) 반영
        if (pendingWriteRef.current) {
          const a = pendingWriteRef.current;
          const same = a.length === ids.length && a.every((v, i) => v === ids[i]);
          if (same) {
            pendingWriteRef.current = null;
          }
        }
        setExcludedIds(ids);
        writeLocal(accountNumber, ids);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [shareBookId, accountNumber]);

  const isIncluded = useCallback(
    (employeeId: string) => !excludedIds.includes(employeeId),
    [excludedIds]
  );

  const setIncluded = useCallback(
    async (employeeId: string, included: boolean) => {
      const next = included
        ? excludedIds.filter((id) => id !== employeeId)
        : Array.from(new Set([...excludedIds, employeeId]));
      // 낙관적 업데이트
      setExcludedIds(next);
      writeLocal(accountNumber, next);
      pendingWriteRef.current = next;

      if (shareBookId) {
        try {
          await setLaborDayExcludedEmployees(shareBookId, accountNumber, next);
        } catch (err) {
          // 실패 시 직전 값 복원은 onSnapshot 이 정정하지만, 즉시 반영도 보장
          console.error('[useLaborDayToggle] Firestore 저장 실패:', err);
        }
      }
    },
    [excludedIds, shareBookId, accountNumber]
  );

  return useMemo(
    () => ({ excludedIds, isIncluded, setIncluded, loading }),
    [excludedIds, isIncluded, setIncluded, loading]
  );
}
